/**
 * Widget load diagnostic beacon.
 *
 * Records each stage of the chatbot widget's lifecycle to
 * public.widget_load_diagnostics via the widget-diagnostic edge function.
 *
 * Fire-and-forget: never blocks the widget, never throws. When a dealer
 * reports "the widget doesn't appear for some visitors", filter this table
 * by dealer_domain + time to see which stage the failing browser reached.
 */
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

const DIAG_STORAGE_KEY = "d2c_widget_diag_id";

export type WidgetStage =
  | "embed-script-loaded"
  | "dealer-auth-requested"
  | "dealer-auth-ok"
  | "dealer-auth-failed"
  | "widget-mounted"
  | "widget-visible"
  | "js-error";

function getOrCreateDiagId(): string {
  try {
    let id = sessionStorage.getItem(DIAG_STORAGE_KEY);
    if (!id) {
      id =
        (crypto?.randomUUID?.() as string | undefined) ??
        `diag_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(DIAG_STORAGE_KEY, id);
    }
    return id;
  } catch {
    return `diag_${Date.now()}`;
  }
}

interface BeaconExtras {
  dealer_domain?: string | null;
  error_message?: string | null;
  error_stack?: string | null;
  extra?: Record<string, unknown> | null;
}

/**
 * Stages that carry actual diagnostic signal are always sent. Happy-path
 * lifecycle stages are sampled, because at fleet scale they were writing
 * ~3.8M rows/day (~2.7 GB/day) into public.widget_load_diagnostics and
 * filled the database disk. Sampling is applied *before* the network call so
 * request volume drops too, not just row count.
 */
const ALWAYS_SEND_STAGES: ReadonlySet<WidgetStage> = new Set<WidgetStage>([
  "js-error",
  "dealer-auth-failed",
]);

const HAPPY_PATH_SAMPLE_RATE = 0.01; // ~1%

/**
 * Sampling is decided once per diag_id (per tab), not per beacon, so a sampled
 * session reports its *whole* funnel. That keeps the data usable for
 * "which stage did this visitor stop at?" instead of leaving holes mid-funnel.
 */
let sampledSession: boolean | null = null;

function isSessionSampled(): boolean {
  if (sampledSession === null) {
    sampledSession = Math.random() < HAPPY_PATH_SAMPLE_RATE;
  }
  return sampledSession;
}

/**
 * Stages that are meaningful at most once per page load. Guards against
 * duplicate emissions from re-renders or a host page initializing the widget
 * more than once.
 */
const ONCE_PER_PAGE_STAGES: ReadonlySet<WidgetStage> = new Set<WidgetStage>([
  "embed-script-loaded",
  "widget-mounted",
  "widget-visible",
]);

/**
 * Kept on `window` rather than in module scope so the guard still holds when
 * two bundles that both include this module (the admin SPA entry and the
 * embedded widget entry) end up on the same page.
 */
function sentOncePerPage(): Set<string> {
  const w = window as unknown as { __d2cDiagSentStages?: Set<string> };
  if (!w.__d2cDiagSentStages) w.__d2cDiagSentStages = new Set<string>();
  return w.__d2cDiagSentStages;
}

export function widgetDiagBeacon(stage: WidgetStage, extras: BeaconExtras = {}): void {
  try {
    if (!SUPABASE_URL || !ANON_KEY) return;

    if (ONCE_PER_PAGE_STAGES.has(stage)) {
      const sent = sentOncePerPage();
      if (sent.has(stage)) return;
      sent.add(stage);
    }

    if (!ALWAYS_SEND_STAGES.has(stage) && !isSessionSampled()) return;


    const body = JSON.stringify({
      diag_id: getOrCreateDiagId(),
      stage,
      dealer_domain:
        extras.dealer_domain ??
        (typeof window !== "undefined" ? window.location.hostname : null),
      page_url: typeof window !== "undefined" ? window.location.href : null,
      referrer: typeof document !== "undefined" ? document.referrer : null,
      ua: typeof navigator !== "undefined" ? navigator.userAgent : null,
      error_message: extras.error_message ?? null,
      error_stack: extras.error_stack ?? null,
      extra: extras.extra ?? null,
    });

    const url = `${SUPABASE_URL}/functions/v1/widget-diagnostic`;

    // Always use fetch with keepalive. sendBeacon() sends credentials
    // (cookies), which the browser rejects against the edge function's
    // wildcard CORS origin — silently dropping every diagnostic beacon.
    void fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: ANON_KEY,
      },
      body,
      keepalive: true,
      // Never let a network failure surface into the widget.
    }).catch(() => {});
  } catch {
    // Diagnostics must never break the widget.
  }
}

let errorListenersInstalled = false;

/**
 * Install top-level `error` / `unhandledrejection` listeners so the *first*
 * JS error on the page is captured and forwarded to the beacon.
 *
 * Only records the first N errors to avoid feedback loops.
 */
export function installWidgetErrorListeners(): void {
  if (errorListenersInstalled || typeof window === "undefined") return;
  errorListenersInstalled = true;

  let remaining = 3;

  window.addEventListener("error", (event) => {
    if (remaining-- <= 0) return;
    widgetDiagBeacon("js-error", {
      error_message: event.message || String(event.error ?? "unknown"),
      error_stack:
        (event.error && (event.error as Error).stack) ||
        `${event.filename}:${event.lineno}:${event.colno}`,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (remaining-- <= 0) return;
    const reason = (event as PromiseRejectionEvent).reason;
    widgetDiagBeacon("js-error", {
      error_message:
        (reason && (reason.message || String(reason))) || "unhandledrejection",
      error_stack: reason && (reason.stack as string | undefined) || null,
    });
  });
}
